/* eslint-disable @typescript-eslint/naming-convention */

import { ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import { PIC } from "@ryde/assets";
import {
  useCrudEditContext,
  useCrudNavigate,
  useRidesActions,
  useTopCalendar,
} from "@ryde/redux";
import {
  ActiveFeedbacksStatusSet,
  capitalize,
  DatesUnit,
  editFormDataAF,
  EditFormMode,
  generateSpecificSearchPattern,
  IRideDto,
  isValidDateTime,
  onActivateKeyDown,
  tableSearchAF,
  TopCalendarMode,
  useDrawers,
  useTimezone,
} from "@ryde/shared";

import { AccessControl } from "../access-control";
import {
  FeedbackCountDropdownSelect,
  RideFeedbacksCountWrapper,
} from "./ride.feedbacks-count.styled";

interface IRideFeedbacksCount extends Partial<IRideDto> {
  shouldHideIcon?: boolean;
  shouldTruncateText?: boolean;
  isCompact?: boolean;
}

const POPOVER_Y_OFFSET = 10;
const entity = "feedback";

export const RideFeedbacksCount: FC<IRideFeedbacksCount> = ({
  id,
  metadata,
  shouldHideIcon,
  shouldTruncateText,
  isCompact,
}) => {
  const rideId = id;
  const { feedbacksCount, relatedFeedbackStatus = {} } = metadata ?? {};
  const {
    id: rideIdFromForm,
    data: { fetchDto },
  } = useCrudEditContext();
  const [isDropdownSelectOpen, setIsDropdownSelectOpen] = useState(false);
  const setFeedbackDto = useSetRecoilState(
    editFormDataAF([entity, EditFormMode.UnboundDrawer]),
  );
  const setTableSearch = useSetRecoilState(tableSearchAF(entity));
  const { navigateToList } = useCrudNavigate(entity);
  const { openEditFormDrawer } = useDrawers();
  const { refetchRide } = useRidesActions();
  const [, startTransition] = useTransition();
  const anchorElement = useRef<HTMLDivElement>(null);
  const { timezone } = useTimezone();
  const { t } = useTranslation();
  const { changeCalendarDates, reInitTopCalendarUnitFromLocationQueryString } =
    useTopCalendar(entity);

  const toggleDropdownSelect = (isOpen?: boolean): void => {
    setIsDropdownSelectOpen(isOpen ?? !isDropdownSelectOpen);
  };

  const {
    feedbacksIds,
    feedbacksMinMaxCreationDate,
    hasAtLastOneActiveFeedback,
  } = useMemo(() => {
    const ids: number[] = [];
    const timestamps: number[] = [];
    let hasActive = false;

    Object.entries(relatedFeedbackStatus).forEach(
      ([feedbackId, { status, createdAt }]) => {
        if (ActiveFeedbacksStatusSet.has(status)) {
          hasActive = true;
        }

        ids.push(Number(feedbackId));

        if (isValidDateTime(createdAt)) {
          timestamps.push(dayjs(createdAt).valueOf());
        }
      },
    );

    let minDate: TDateISO | undefined;
    let maxDate: TDateISO | undefined;

    if (timestamps.length) {
      minDate = dayjs(Math.min(...timestamps)).toISOString() as TDateISO;
      maxDate = dayjs(Math.max(...timestamps)).toISOString() as TDateISO;
    }

    return {
      feedbacksIds: ids,
      hasAtLastOneActiveFeedback: hasActive,
      feedbacksMinMaxCreationDate: {
        minDate,
        maxDate,
      },
    };
  }, [relatedFeedbackStatus]);

  const translateLabel = useMemo(() => {
    switch (true) {
      case feedbacksCount && !shouldTruncateText:
        return `${feedbacksCount} ${capitalize(t("feedback.plural"))}`;
      case !feedbacksCount && !shouldTruncateText:
        return `${capitalize(t("feedback.plural"))}`;
      case feedbacksCount && shouldTruncateText:
        return `${feedbacksCount}`;
      default:
        return `+`;
    }
  }, [feedbacksCount, shouldTruncateText, t]);

  const navigateToFeedbacks = useCallback(() => {
    if (feedbacksCount && feedbacksIds?.length) {
      const dates: DatesUnit = {
        date1: dayjs(feedbacksMinMaxCreationDate?.minDate)
          .tz(timezone)
          .startOf("day")
          .toISOString() as TDateISO,
        date2: dayjs(feedbacksMinMaxCreationDate?.maxDate)
          .tz(timezone)
          .endOf("day")
          .toISOString() as TDateISO,
      };

      reInitTopCalendarUnitFromLocationQueryString();
      changeCalendarDates(dates, TopCalendarMode.CUSTOM);
      setTableSearch(generateSpecificSearchPattern(feedbacksIds));
    }

    navigateToList();
  }, [
    feedbacksCount,
    feedbacksIds,
    timezone,
    feedbacksMinMaxCreationDate,
    reInitTopCalendarUnitFromLocationQueryString,
    changeCalendarDates,
    navigateToList,
    setTableSearch,
  ]);

  const addNewFeedback = useCallback(async (): Promise<void> => {
    if (rideId) {
      setFeedbackDto({ rideId });
      const simpleResult = await openEditFormDrawer({
        id: undefined,
        entity,
      });

      if (simpleResult.success) {
        startTransition(() => {
          if (rideIdFromForm) {
            fetchDto?.(rideId);
          }

          refetchRide(rideId);
        });
      }
    }
  }, [
    fetchDto,
    openEditFormDrawer,
    refetchRide,
    rideId,
    rideIdFromForm,
    setFeedbackDto,
  ]);

  return (
    <>
      <RideFeedbacksCountWrapper
        ref={anchorElement}
        className={clsx({ "is-compact": isCompact })}
        onClick={() => toggleDropdownSelect()}
      >
        <span
          className={clsx("feedback-count-icon-container", {
            "should-hide-icon": shouldHideIcon,
          })}
        >
          {Boolean(hasAtLastOneActiveFeedback) && <PIC.MessageSvg />}
          {Boolean(!hasAtLastOneActiveFeedback) && <PIC.MessageGreyedSvg />}
        </span>

        <span
          className={clsx("feedbacks-count-label", {
            blueColor: shouldTruncateText,
          })}
        >
          {translateLabel}
        </span>
      </RideFeedbacksCountWrapper>

      <Popper
        anchorEl={anchorElement.current}
        open={isDropdownSelectOpen}
        placement={"bottom-start"}
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, POPOVER_Y_OFFSET],
              },
            },
          ],
        }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper onClick={() => toggleDropdownSelect(false)}>
              <ClickAwayListener
                onClickAway={() => toggleDropdownSelect(false)}
              >
                <FeedbackCountDropdownSelect>
                  <AccessControl action="create" entity={entity}>
                    <div
                      className="feedback-count-option-item"
                      onClick={addNewFeedback}
                      onKeyDown={onActivateKeyDown(addNewFeedback)}
                      role="button"
                      tabIndex={0}
                    >
                      {t("feedback.ADD_FEEDBACK")}
                    </div>
                  </AccessControl>
                  <AccessControl action="read" entity={entity}>
                    <div
                      className="feedback-count-option-item"
                      onClick={navigateToFeedbacks}
                      onKeyDown={onActivateKeyDown(navigateToFeedbacks)}
                      role="button"
                      tabIndex={0}
                    >
                      {t("feedback.GO_TO_FEEDBACKS")}
                    </div>
                  </AccessControl>
                </FeedbackCountDropdownSelect>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
